import i18next from "i18next";

import { formatDate } from "@ergon-ai/utils";

/**
 * Formats a date into a human-readable relative time string with contextual fallbacks.
 *
 * - Returns `"Today"` if the date is the current day.
 * - Returns `"Yesterday"` if the date is the previous day.
 * - Returns `"X days ago"` for dates within the last 7 days.
 * - Returns `"X weeks ago"` for dates within the last 30 days.
 * - Returns `"X months ago"` for dates within the last year.
 * - Returns a formatted absolute date for dates older than 1 year.
 *
 * This is useful for displaying review dates, activity timestamps, or any content
 * where recent dates need relative context but old dates need absolute clarity.
 *
 * @param date - The date to format (Date object or ISO string).
 * @param locale - The locale string (e.g., `"en-GB"`, `"it-IT"`). Defaults to `"en-GB"`.
 * @returns A localized string representing the relative or formatted date.
 *
 * @example
 * ```ts
 * // Assuming today is 2025-02-04
 * getFormattedRelativeDate(new Date("2025-02-04")); // "Today"
 * getFormattedRelativeDate("2025-02-03"); // "Yesterday"
 * getFormattedRelativeDate(new Date("2025-01-30")); // "5 days ago"
 * getFormattedRelativeDate(new Date("2025-01-15")); // "2 weeks ago"
 * getFormattedRelativeDate(new Date("2024-10-10")); // "3 months ago"
 * getFormattedRelativeDate(new Date("2023-01-15"), "en-GB"); // "15/01/2023"
 * ```
 */
export const getFormattedRelativeDate = (
  date: Date | string,
  locale = "en-GB",
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) return i18next.t("date.today");
  if (diffInDays === 1) return i18next.t("date.yesterday");
  if (diffInDays < 7) return i18next.t("date.daysAgo", { count: diffInDays });
  if (diffInDays < 30)
    return i18next.t("date.weeksAgo", { count: Math.floor(diffInDays / 7) });
  if (diffInDays < 365)
    return i18next.t("date.monthsAgo", { count: Math.floor(diffInDays / 30) });

  return formatDate(dateObj, locale);
};
